import React, { useState, useEffect } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from 'react-router-dom';
import { CatalogueMainHeader } from '../../components/catalogue/CatalogueMainHeader';
import { ChooseSoundworldModalSimple } from '../../components/catalogue/ChooseSoundworldModalSimple';
import { useSoundworlds } from '../../hooks/useSoundworlds';
import { useProjectCreation } from '../../hooks/useProjectCreation';
import toast from 'react-hot-toast';

export const NewTrackPage = ({ username }) => {
    const { isAuthenticated, isLoading } = useAuth0();
    const navigate = useNavigate();
    const [showModal, setShowModal] = useState(false);
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [isGenerating, setIsGenerating] = useState(false);
    const [generatedPiece, setGeneratedPiece] = useState<any>(null);

    const { soundworlds, generatePreview, getMidiUrl } = useSoundworlds();
    const { formData, setFormData, createProject } = useProjectCreation();

    useEffect(() => {
        if (!isAuthenticated && !isLoading) {
            navigate('/');
            return;
        }
    }, [isAuthenticated, isLoading, navigate]);

    const handleTagSelect = (tag: string) => {
        setSelectedTags(prev =>
            prev.includes(tag)
                ? prev.filter(t => t !== tag)
                : [...prev, tag]
        );
    };

    const handleGenerate = async () => {
        if (selectedTags.length === 0) {
            toast.error('Please select at least one tag');
            return;
        }

        setIsGenerating(true);
        try {
            const piece = await generatePreview(selectedTags);
            setGeneratedPiece(piece);

            // Create a project for this piece
            const projectId = await createProject('soundworld');
            if (projectId) {
                // Navigate to project details
                navigate(`/catalogue/${projectId}`);
            }
        } catch (error) {
            toast.error('Failed to generate preview');
        } finally {
            setIsGenerating(false);
        }
    };

    const handleSoundworldSelect = (soundworld: string) => {
        setFormData(prev => ({ ...prev, soundworld }));
        setShowModal(false);
    };

    // Common music tags
    const commonTags = [
        'piano', 'strings', 'drums', 'bass', 'synth', 'guitar',
        'ambient', 'electronic', 'classical', 'jazz', 'rock', 'pop',
        'chill', 'energetic', 'melodic', 'rhythmic', 'atmospheric'
    ];

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin"></div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return null;
    }

    return (
        <div className="min-h-screen bg-background">
            <CatalogueMainHeader username={ username } backButton />

            <div className="flex flex-col items-center mt-24 justify-center px-8">
                <h1 className="text-3xl font-semibold text-center mb-4 text-card-foreground">
                    Create New Track
                </h1>
                <p className="text-sm text-muted-foreground mb-8 text-center max-w-md">
                    Select tags to describe your desired track and let AI generate it for you.
                </p>

                {/* Soundworld Selection */ }
                <div className="mb-8">
                    <span className="text-sm font-medium mr-2">Choose a soundworld:</span>
                    <button
                        onClick={ () => setShowModal(true) }
                        className="bg-gray-100 px-3 py-1 rounded text-sm hover:bg-gray-200"
                    >
                        { formData.soundworld }
                    </button>
                </div>

                {/* Tag Selection */ }
                <div className="w-full max-w-2xl mb-8">
                    <h3 className="text-lg font-medium mb-4 text-center">Select Tags</h3>
                    <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                        { commonTags.map((tag) => (
                            <button
                                key={ tag }
                                onClick={ () => handleTagSelect(tag) }
                                className={ `px-3 py-2 rounded text-sm transition-colors ${selectedTags.includes(tag)
                                    ? 'bg-black text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }` }
                            >
                                { tag }
                            </button>
                        )) }
                    </div>
                </div>

                {/* Selected Tags */ }
                { selectedTags.length > 0 && (
                    <div className="mb-8">
                        <h4 className="text-sm font-medium mb-2">Selected Tags:</h4>
                        <div className="flex flex-wrap gap-2">
                            { selectedTags.map((tag) => (
                                <span
                                    key={ tag }
                                    className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs"
                                >
                                    { tag }
                                </span>
                            )) }
                        </div>
                    </div>
                ) }

                {/* Generate Button */ }
                <button
                    onClick={ handleGenerate }
                    disabled={ selectedTags.length === 0 || isGenerating }
                    className={ `px-8 py-3 rounded-lg flex items-center gap-2 transition-colors ${selectedTags.length === 0 || isGenerating
                        ? 'bg-muted text-muted-foreground cursor-not-allowed'
                        : 'btn-primary'
                        }` }
                >
                    { isGenerating ? (
                        <>
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            Generating...
                        </>
                    ) : (
                        'Generate Track'
                    ) }
                </button>

                {/* Generated Piece Info */ }
                { generatedPiece && (
                    <div className="mt-8 p-4 bg-green-50 border border-green-200 rounded-lg">
                        <h4 className="font-medium text-green-800 mb-2">Generated Successfully!</h4>
                        <p className="text-sm text-green-700">
                            Piece ID: { generatedPiece.piece_id }
                        </p>
                        { generatedPiece.url && (
                            <p className="text-sm text-green-700">
                                Audio URL: { generatedPiece.url }
                            </p>
                        ) }
                    </div>
                ) }
            </div>

            { showModal && (
                <ChooseSoundworldModalSimple
                    isOpen={ showModal }
                    onClose={ () => setShowModal(false) }
                    onSelect={ handleSoundworldSelect }
                />
            ) }
        </div>
    );
}; 